export const WEB_BUILD_META_NAME = 'intez-build';
export const WEB_BUILD_UPDATE_STORAGE_KEY = 'intez:web-build-target';

export type WebBuildUpdatePhase =
  | 'CURRENT'
  | 'WAITING_FOR_CALL'
  | 'READY_TO_RELOAD';

export type WebBuildUpdateSnapshot = {
  phase: WebBuildUpdatePhase;
};

type WebBuildUpdateDependencies = {
  phoneProtected: () => boolean;
  reload: () => void;
};

type WebBuildAnnouncement = {
  targetBuildId: string;
  detectedAt: number;
};

function attributeValue(tag: string, name: string): string | null {
  const expression = new RegExp(`\\b${name}\\s*=\\s*(["'])(.*?)\\1`, 'i');
  return tag.match(expression)?.[2]?.trim() || null;
}

function validBuildId(value: unknown): value is string {
  return typeof value === 'string'
    && /^[a-z0-9._:-]{4,128}$/i.test(value);
}

export function buildFingerprint(html: string): string | null {
  const tags = html.match(/<meta\b[^>]*>/gi) || [];
  for (const tag of tags) {
    if (attributeValue(tag, 'name')?.toLowerCase() !== WEB_BUILD_META_NAME) continue;
    const fingerprint = attributeValue(tag, 'content');
    return validBuildId(fingerprint) ? fingerprint : null;
  }
  return null;
}

export function hasWebBuildChanged(currentBuildId: string, indexHtml: string): boolean {
  const nextBuildId = buildFingerprint(indexHtml);
  return Boolean(validBuildId(currentBuildId) && nextBuildId && currentBuildId !== nextBuildId);
}

export function createWebBuildAnnouncement(targetBuildId: string, detectedAt = Date.now()): string {
  if (!validBuildId(targetBuildId)) throw new Error('Некорректный идентификатор Web-сборки');
  return JSON.stringify({ targetBuildId, detectedAt } satisfies WebBuildAnnouncement);
}

export function announcedBuildTarget(serialized: string | null): string | null {
  if (!serialized) return null;
  try {
    const announcement = JSON.parse(serialized) as Partial<WebBuildAnnouncement>;
    return validBuildId(announcement.targetBuildId) ? announcement.targetBuildId : null;
  } catch {
    return null;
  }
}

export type WebBuildTargetResolution = {
  targetBuildId: string | null;
  source: 'server' | 'shared' | 'none';
  changed: boolean;
};

export function resolveWebBuildTarget(
  currentBuildId: string,
  serverIndexHtml: string | null,
  sharedAnnouncement: string | null,
): WebBuildTargetResolution {
  const serverBuildId = serverIndexHtml === null
    ? null
    : buildFingerprint(serverIndexHtml);
  const sharedBuildId = announcedBuildTarget(sharedAnnouncement);
  const targetBuildId = serverBuildId || sharedBuildId;
  const source = serverBuildId
    ? 'server'
    : sharedBuildId
      ? 'shared'
      : 'none';

  return {
    targetBuildId,
    source,
    changed: Boolean(targetBuildId && targetBuildId !== currentBuildId),
  };
}

export class WebBuildUpdateController {
  private buildChanged = false;
  private phase: WebBuildUpdatePhase = 'CURRENT';
  private readonly listeners = new Set<(snapshot: WebBuildUpdateSnapshot) => void>();

  constructor(private readonly dependencies: WebBuildUpdateDependencies) {}

  getSnapshot(): WebBuildUpdateSnapshot {
    return { phase: this.phase };
  }

  subscribe(listener: (snapshot: WebBuildUpdateSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async reportBuildChanged(): Promise<void> {
    this.buildChanged = true;
    await this.reconcile();
  }

  async reportBuildCurrent(): Promise<void> {
    this.buildChanged = false;
    this.setPhase('CURRENT');
  }

  async phoneStateChanged(): Promise<void> {
    await this.reconcile();
  }

  async applyUpdate(): Promise<boolean> {
    if (!this.buildChanged) return false;
    if (this.dependencies.phoneProtected()) {
      this.setPhase('WAITING_FOR_CALL');
      return false;
    }

    this.setPhase('READY_TO_RELOAD');
    this.dependencies.reload();
    return true;
  }

  private async reconcile(): Promise<void> {
    if (!this.buildChanged || this.phase === 'READY_TO_RELOAD') return;

    if (this.dependencies.phoneProtected()) {
      this.setPhase('WAITING_FOR_CALL');
      return;
    }

    this.setPhase('READY_TO_RELOAD');
    this.dependencies.reload();
  }

  private setPhase(phase: WebBuildUpdatePhase): void {
    if (this.phase === phase) return;
    this.phase = phase;
    const snapshot = this.getSnapshot();
    this.listeners.forEach(listener => listener(snapshot));
  }
}
